import { C, STAGES } from "../../../../constants";
import { daysUntil, fmtMoney, getInit, projectPriority } from "../../../../utils";

export default function DashboardPage({
  deals,
  projects,
  tasks,
  consultants,
  nav,
  KpiCardComponent,
  AvatarComponent,
  PriorityBadgeComponent,
}) {
  const pipeline = deals.reduce((sum, deal) => sum + deal.dealValue, 0);
  const weighted = deals.reduce((sum, deal) => sum + deal.dealValue * (deal.probability / 100), 0);
  const avgUtil = projects.length > 0 ? Math.round(projects.reduce((sum, project) => sum + (project.utilizationPct || 0), 0) / projects.length) : 0;
  const totalBill = projects.reduce((sum, project) => sum + project.billables, 0);
  const critical = projects.filter((project) => project.projectStatus === "Critical");
  const atRisk = projects.filter((project) => project.projectStatus === "At Risk");
  const overdue = projects.filter((project) => project.weeklyReportStatus === "Overdue");
  const unassigned = deals.filter((deal) => !deal.assignedConsultant);
  const renewals = projects.filter((project) => {
    const days = daysUntil(project.contractEndDate);
    return days <= 90 && days > 0;
  });
  const renewalVal = renewals.reduce((sum, project) => sum + (project.billables * (project.renewalProbability / 100)), 0);
  const benchCount = consultants ? consultants.filter((consultant) => consultant.status === "Bench").length : 0;
  const partialCount = consultants ? consultants.filter((consultant) => consultant.status === "Partial").length : 0;
  const rollOffCount = consultants ? consultants.filter((consultant) => consultant.projectEndDate && daysUntil(consultant.projectEndDate) <= 30 && daysUntil(consultant.projectEndDate) >= 0).length : 0;
  const avgConsultantUtil = consultants && consultants.length > 0 ? Math.round(consultants.reduce((sum, consultant) => sum + consultant.utilizationPct, 0) / consultants.length) : 0;
  const openTasks = tasks.filter((task) => task.status !== "Done" && task.status !== "Cancelled");
  const overdueTasks = openTasks.filter((task) => daysUntil(task.dueDate) < 0);
  const blockedTasks = openTasks.filter((task) => task.status === "Blocked");
  const consultantMap = {};

  projects.forEach((project) => {
    project.assignedConsultants.forEach((consultant) => {
      if (!consultantMap[consultant]) {
        consultantMap[consultant] = { util: 0 };
      }
      consultantMap[consultant].util = Math.max(consultantMap[consultant].util, project.utilizationPct);
    });
  });

  const stageBreakdown = STAGES.map((stage) => ({
    stage,
    count: deals.filter((deal) => deal.stage === stage).length,
    value: deals.filter((deal) => deal.stage === stage).reduce((sum, deal) => sum + deal.dealValue, 0),
  }));

  return (
    <div className="fade">
      <div className="pg-top"><div className="pg-title">Dashboard</div><div className="pg-sub">Operational overview · March 2026</div></div>
      <div className="pg-body">
        <div className="krow">
          <KpiCardComponent label="Pipeline Value" value={fmtMoney(pipeline)} trend="+12%" up onClick={() => nav("pipeline")} />
          <KpiCardComponent label="Active Revenue" value={fmtMoney(totalBill)} trend="+8%" up onClick={() => nav("projects")} />
          <KpiCardComponent label="Team Utilisation" value={`${avgUtil}%`} trend="−3pt" up={false} onClick={() => nav("projects")} />
          <KpiCardComponent label="Weighted Pipeline" value={fmtMoney(weighted)} sub="Prob-adjusted" />
        </div>

        {(critical.length > 0 || overdueTasks.length > 0 || blockedTasks.length > 0 || overdue.length > 0 || unassigned.length > 0) && (
          <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
            {critical.length > 0 && <span className="bcrit">⚠ {critical.length} Critical Project{critical.length > 1 ? "s" : ""}</span>}
            {overdueTasks.length > 0 && <span className="bcrit">⚠ {overdueTasks.length} Overdue Task{overdueTasks.length > 1 ? "s" : ""}</span>}
            {blockedTasks.length > 0 && <span className="bwarn">⊘ {blockedTasks.length} Blocked Task{blockedTasks.length > 1 ? "s" : ""}</span>}
            {overdue.length > 0 && <span className="bcrit">⚠ {overdue.length} Overdue Report{overdue.length > 1 ? "s" : ""}</span>}
            {unassigned.length > 0 && <span className="bwarn">● {unassigned.length} Unassigned Deal{unassigned.length > 1 ? "s" : ""}</span>}
          </div>
        )}

        <div className="d3">
          <div className="panel">
            <div className="panel-head"><span className="panel-title">Project Health</span><span className="panel-link" onClick={() => nav("projects")}>View all →</span></div>
            <div style={{ padding: "14px 18px" }}>
              {[["On Track", projects.filter((project) => project.projectStatus === "On Track").length, C.green], ["At Risk", atRisk.length, C.yellow], ["Critical", critical.length, C.red]].map(([label, count, color]) => (
                <div className="sbar-row" key={label}>
                  <span className="sbar-lbl" style={{ color, fontWeight: 600, fontSize: 12 }}>{label}</span>
                  <div className="sbar-track"><div className="sbar-fill" style={{ width: `${(count / projects.length) * 100}%`, background: color }} /></div>
                  <span className="sbar-val">{count}</span>
                </div>
              ))}
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #0f1a30" }}>
                {[["Open Tasks", openTasks.length, false], ["Overdue Tasks", overdueTasks.length, overdueTasks.length > 0], ["Blocked Tasks", blockedTasks.length, blockedTasks.length > 0]].map(([key, value, hot]) => (
                  <div className="frow" key={key}><span style={{ color: C.sub, fontSize: 12.5 }}>{key}</span><span className="mono" style={{ fontWeight: 600, color: hot ? C.red : C.text }}>{value}</span></div>
                ))}
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-head"><span className="panel-title">Resource &amp; Pipeline</span><span className="panel-link" onClick={() => nav("pipeline")}>Pipeline →</span></div>
            <div style={{ padding: "12px 18px" }}>
              <div style={{ fontSize: "10px", fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8 }}>Consultant Allocation</div>
              {Object.entries(consultantMap).slice(0, 4).map(([name, data]) => (
                <div className="cons-row" key={name}>
                  <AvatarComponent initials={getInit(name)} size={20} />
                  <span style={{ flex: 1, color: C.sub, fontSize: 12 }}>{name}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{ width: 54, background: "#14213d", borderRadius: 3, height: 4 }}><div style={{ width: `${data.util}%`, height: "100%", borderRadius: 3, background: data.util >= 80 ? C.green : data.util >= 60 ? C.yellow : C.red }} /></div>
                    <span className="mono" style={{ fontSize: 11, color: C.text }}>{data.util}%</span>
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #0f1a30" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 7 }}>Lead Funnel</div>
                {stageBreakdown.map((stage) => (
                  <div className="sbar-row" key={stage.stage} style={{ padding: "3px 0" }}>
                    <span style={{ fontSize: 10.5, color: C.sub, width: 120, flexShrink: 0 }}>{stage.stage}</span>
                    <div className="sbar-track"><div className="sbar-fill" style={{ width: `${(stage.count / deals.length) * 100}%`, background: "#3b7ffa" }} /></div>
                    <span className="sbar-val">{stage.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-head"><span className="panel-title">Forecast</span></div>
            <div style={{ padding: "14px 18px" }}>
              {[["Active Billables", fmtMoney(totalBill)], ["Weighted Pipeline", fmtMoney(weighted)], ["Renewal Est. (90d)", fmtMoney(renewalVal)], ["Q2 Revenue Forecast", fmtMoney(weighted + renewalVal)]].map(([key, value]) => (
                <div className="frow" key={key}><span style={{ color: C.sub, fontSize: 12.5 }}>{key}</span><span className="mono" style={{ fontWeight: 600, color: key.includes("Forecast") ? C.green : C.text }}>{value}</span></div>
              ))}
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #0f1a30" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8 }}>Upcoming Renewals</div>
                {renewals.length === 0 ? <div style={{ fontSize: 12, color: C.dim }}>None in 90 days</div> : renewals.slice(0, 3).map((project) => (
                  <div className="cons-row" key={project.projectId} onClick={() => nav("project", project.projectId)} style={{ cursor: "pointer" }}>
                    <div style={{ flex: 1 }}><div style={{ fontSize: 12, color: C.text }}>{project.projectName}</div><div style={{ fontSize: 10.5, color: C.sub }}>{project.clientName}</div></div>
                    <div style={{ textAlign: "right" }}><span className="bwarn">{daysUntil(project.contractEndDate)}d</span><div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>{project.renewalProbability}% likely</div></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="d2">
          <div className="panel">
            <div className="panel-head"><span className="panel-title">Recent PreSales Activity</span><span className="panel-link" onClick={() => nav("pipeline")}>View all →</span></div>
            {deals.slice(0, 5).map((deal) => (
              <div className="panel-row" key={deal.dealId} onClick={() => nav("deal", deal.dealId)}>
                <div><div style={{ fontWeight: 600, color: C.text, fontSize: 13 }}>{deal.companyName}</div><div style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>{deal.stage}</div></div>
                <div style={{ textAlign: "right" }}><div className="mono" style={{ fontSize: 13, color: C.blue }}>{fmtMoney(deal.dealValue)}</div>{!deal.assignedConsultant && <span className="bwarn" style={{ marginTop: 3, display: "inline-flex" }}>Unassigned</span>}</div>
              </div>
            ))}
          </div>
          <div className="panel">
            <div className="panel-head"><span className="panel-title">Priority Projects</span><span className="panel-link" onClick={() => nav("projects")}>View all →</span></div>
            {[...projects].sort((left, right) => projectPriority(right) - projectPriority(left)).slice(0, 6).map((project) => (
              <div className="panel-row" key={project.projectId} onClick={() => nav("project", project.projectId)}>
                <div style={{ flex: 1 }}><div style={{ fontWeight: 600, color: C.text, fontSize: 13 }}>{project.projectName}</div><div style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>{project.clientName}</div></div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 10 }}>
                  {(() => {
                    // Treat 0 as "not set" so we fall through to client NPS.
                    const pn = typeof project.NPS === "number" && project.NPS > 0 ? project.NPS : null;
                    const cn = typeof project.clientNps === "number" && project.clientNps > 0 ? project.clientNps : null;
                    const n = pn ?? cn;
                    return n != null
                      ? <span className="mono" style={{ fontSize: 12, color: n >= 4 ? C.green : n >= 3 ? C.yellow : C.red }}>{n}</span>
                      : <span className="mono" style={{ fontSize: 12, color: C.dim }}>—</span>;
                  })()}
                  <PriorityBadgeComponent score={projectPriority(project)} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {consultants && consultants.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 10, display: "flex", alignItems: "center", gap: 7 }}>◇ Resource Summary</div>
            <div className="krow" style={{ marginBottom: 0 }}>
              <KpiCardComponent label="On Bench" value={benchCount} accent={benchCount > 0 ? C.purple : C.green} onClick={() => nav("resources")} />
              <KpiCardComponent label="Partial Allocation" value={partialCount} accent={C.yellow} onClick={() => nav("resources")} />
              <KpiCardComponent label="Rolling Off (30d)" value={rollOffCount} accent={rollOffCount > 0 ? C.yellow : C.green} onClick={() => nav("resources")} />
              <KpiCardComponent label="Avg Utilisation" value={`${avgConsultantUtil}%`} accent={avgConsultantUtil >= 70 ? C.green : avgConsultantUtil >= 50 ? C.yellow : C.red} onClick={() => nav("resources")} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
